import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Container, Typography, Paper, Tabs, Tab,
  TextField, InputAdornment, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, Alert, Snackbar, Divider,
  ToggleButton, ToggleButtonGroup, Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  PersonAdd as PersonAddIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

// ---- helpers -------------------------------------------------------

const STATUS_META = {
  contacted:      { label: 'Contacted',      color: 'info' },
  follow_up:      { label: 'Follow Up',       color: 'warning' },
  not_interested: { label: 'Not Interested',  color: 'error' },
  offer_created:  { label: 'Offer Pending',   color: 'secondary' },
  converted:      { label: 'Converted',       color: 'success' },
};

function StatusChip({ status, size = 'small' }) {
  const meta = STATUS_META[status] || { label: status, color: 'default' };
  return <Chip label={meta.label} color={meta.color} size={size} variant="outlined" />;
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ====================================================================
// Main component
// ====================================================================
export default function BusinessOutreach() {
  const { getToken, user } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ---- Tab 1: Contact History Check --------------------------------
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ organisations: [], contacts: [], results: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactsTotal, setContactsTotal] = useState(0);

  // Dialog: quick-action (follow-up date / not interested)
  const [actionDialog, setActionDialog] = useState({ open: false, contact: null, mode: null });
  const [actionDate, setActionDate] = useState('');
  const [actionNotes, setActionNotes] = useState('');

  // ---- Tab 2: Reactivation List ------------------------------------
  const [reactivation, setReactivation] = useState([]);
  const [reactivationLoading, setReactivationLoading] = useState(false);
  const [reactivationSearch, setReactivationSearch] = useState('');

  const searchTimer = useRef(null);

  // ------------------------------------------------------------------
  // Auth header helper
  // ------------------------------------------------------------------
  const authHeaders = useCallback(async (extra = {}) => {
    const token = await getToken();
    const { fetchCSRFToken } = await import('../utils/csrfProtection');
    const csrf = await fetchCSRFToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
      ...extra,
    };
  }, [getToken]);

  const readHeaders = useCallback(async () => {
    const token = await getToken();
    return { 'Authorization': `Bearer ${token}` };
  }, [getToken]);

  // ------------------------------------------------------------------
  // Load contacts list
  // ------------------------------------------------------------------
  const loadContacts = useCallback(async (status = 'all') => {
    setContactsLoading(true);
    try {
      const headers = await readHeaders();
      const params = new URLSearchParams({ status, limit: 100 });
      const res = await fetch(`${API_URL}/outreach/contacts?${params}`, { headers });
      const data = await res.json();
      if (data.success) {
        setContacts(data.data);
        setContactsTotal(data.total);
      } else {
        showSnackbar(`Failed to load contacts: ${data.message || 'Unknown server error'}`, 'error');
      }
    } catch (err) {
      showSnackbar('Failed to load contacts', 'error');
    } finally {
      setContactsLoading(false);
    }
  }, [readHeaders]);

  // ------------------------------------------------------------------
  // Load reactivation list
  // ------------------------------------------------------------------
  const loadReactivation = useCallback(async () => {
    setReactivationLoading(true);
    try {
      const headers = await readHeaders();
      const res = await fetch(`${API_URL}/outreach/reactivation`, { headers });
      const data = await res.json();
      if (data.success) setReactivation(data.data);
    } catch (err) {
      showSnackbar('Failed to load reactivation list', 'error');
    } finally {
      setReactivationLoading(false);
    }
  }, [readHeaders]);

  useEffect(() => {
    loadContacts(statusFilter);
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => {
    if (tab === 1) loadReactivation();
  }, [tab]); // eslint-disable-line

  // ------------------------------------------------------------------
  // Search (debounced)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults({ organisations: [], contacts: [], results: [] });
      return;
    }
    clearTimeout(searchTimer.current);
    setSearchLoading(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const headers = await readHeaders();
        const res = await fetch(`${API_URL}/outreach/search?q=${encodeURIComponent(searchQuery)}`, { headers });
        const data = await res.json();
        if (data.success) setSearchResults({ organisations: data.organisations, contacts: data.contacts, results: data.results || [] });
      } catch {
        // silent
      } finally {
        setSearchLoading(false);
      }
    }, 350);
  }, [searchQuery]); // eslint-disable-line

  // ------------------------------------------------------------------
  // Snackbar
  // ------------------------------------------------------------------
  function showSnackbar(message, severity = 'success') {
    setSnackbar({ open: true, message, severity });
  }

  // ------------------------------------------------------------------
  // Navigation helpers
  // ------------------------------------------------------------------
  function openAddForm(prefill = {}) {
    navigate('/app/outreach/add', { state: { prefill } });
  }

  function openEditForm(contact) {
    navigate(`/app/outreach/edit/${contact.id}`, { state: { contact } });
  }

  // Only the creator or an admin can manage a contact record.
  const canManage = (c) =>
    user?.role === 'admin' || c.contacted_by === user?.id;

  // ------------------------------------------------------------------
  // Quick status updates
  // ------------------------------------------------------------------
  async function updateStatus(contactId, status, extra = {}) {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_URL}/outreach/contacts/${contactId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status, ...extra }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      loadContacts(statusFilter);
      showSnackbar('Status updated');
    } catch (err) {
      showSnackbar(err.message || 'Update failed', 'error');
    }
  }

  async function deleteContact(id) {
    if (!window.confirm('Delete this contact record?')) return;
    try {
      const headers = await authHeaders();
      await fetch(`${API_URL}/outreach/contacts/${id}`, { method: 'DELETE', headers });
      loadContacts(statusFilter);
      showSnackbar('Contact deleted');
    } catch {
      showSnackbar('Delete failed', 'error');
    }
  }

  // ------------------------------------------------------------------
  // Action dialog submit
  // ------------------------------------------------------------------
  async function submitActionDialog() {
    const { contact, mode } = actionDialog;
    const payload = mode === 'follow_up'
      ? { status: 'follow_up', follow_up_date: actionDate || null, notes: actionNotes || contact.notes }
      : { status: 'not_interested', notes: actionNotes || contact.notes };
    await updateStatus(contact.id, payload.status, payload);
    setActionDialog({ open: false, contact: null, mode: null });
    setActionDate('');
    setActionNotes('');
  }

  // ------------------------------------------------------------------
  // Convert to offer
  // ------------------------------------------------------------------
  function convertToOffer(contact) {
    if (contact.organization_id) {
      navigate('/app/sales-workflow', {
        state: {
          openCreateOffer: true,
          prefillOrg: { id: contact.organization_id, name: contact.business_name },
        },
      });
    } else {
      navigate('/app/sales-workflow', {
        state: {
          openCreateOffer: true,
          prefillOrg: {
            id: null,
            name: contact.business_name,
            primaryEmail: contact.contact_email,
            primaryPhone: contact.contact_phone,
          },
        },
      });
    }
    // Mark as converted in background
    updateStatus(contact.id, 'converted');
  }

  function convertOrgToOffer(org) {
    navigate('/app/sales-workflow', {
      state: {
        openCreateOffer: true,
        prefillOrg: {
          id: org.id,
          name: org.name,
          primaryEmail: org.primaryEmail,
          primaryPhone: org.primaryPhone,
        },
      },
    });
  }

  // ------------------------------------------------------------------
  // Add reactivation org to outreach list
  // ------------------------------------------------------------------
  async function addReactivationToOutreach(org) {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_URL}/outreach/contacts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          business_name:   org.name,
          contact_email:   org.primaryEmail || '',
          contact_phone:   org.primaryPhone || '',
          suburb:          org.suburb || '',
          state:           org.state  || '',
          organization_id: org.id,
          status:          'contacted',
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      showSnackbar(`${org.name} added to Contact History`);
    } catch (err) {
      showSnackbar(err.message || 'Failed to add', 'error');
    }
  }

  // ------------------------------------------------------------------
  // Filtered reactivation list
  // ------------------------------------------------------------------
  const filteredReactivation = reactivationSearch.trim().length > 0
    ? reactivation.filter(o =>
        o.name?.toLowerCase().includes(reactivationSearch.toLowerCase()) ||
        o.suburb?.toLowerCase().includes(reactivationSearch.toLowerCase()) ||
        o.salesConsultantName?.toLowerCase().includes(reactivationSearch.toLowerCase())
      )
    : reactivation;

  // ==================================================================
  // Render
  // ==================================================================
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Business Outreach</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Track cold contacts, follow-ups, and reactivate dormant organisations.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Contact History Check" />
        <Tab
          label={
            <Badge badgeContent={reactivation.length || null} color="warning" max={999}>
              Reactivation List
            </Badge>
          }
        />
      </Tabs>

      {/* ============================================================
          TAB 0 — Contact History Check
      ============================================================ */}
      {tab === 0 && (
        <Box>
          {/* Search bar */}
          <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Autocomplete
                sx={{ flex: 1 }}
                freeSolo
                inputValue={searchQuery}
                onInputChange={(_, val) => setSearchQuery(val)}
                filterOptions={(x) => x}
                options={
                  searchResults.results.map(item => ({
                    key:        `${item._type}-${item.id}`,
                    label:      item._type === 'org' ? item.name : item.business_name,
                    sublabel:   [item.suburb, item.state].filter(Boolean).join(', '),
                    badge:      item._type === 'org'
                                  ? 'Existing Org'
                                  : (STATUS_META[item.status]?.label || item.status),
                    badgeColor: item._type === 'org'
                                  ? 'primary'
                                  : (STATUS_META[item.status]?.color || 'default'),
                    contact:    item._type === 'org'
                                  ? (item.primaryEmail || item.primaryPhone)
                                  : (item.contact_email || item.contact_phone),
                    _type:  item._type,
                    _data:  item,
                  }))
                }
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
                renderOption={(props, opt) => {
                  const { key, ...rest } = props;
                  return (
                    <Box component="li" key={key} {...rest} sx={{ flexDirection: 'column', alignItems: 'flex-start !important', py: '6px !important' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                        <Chip label={opt.badge} color={opt.badgeColor} size="small" sx={{ ml: 'auto' }} />
                      </Box>
                      {opt.sublabel && (
                        <Typography variant="caption" color="text.secondary">{opt.sublabel}</Typography>
                      )}
                      {opt.contact && (
                        <Typography variant="caption" color="text.secondary">{opt.contact}</Typography>
                      )}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by business name, suburb or email…"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          {searchLoading ? <CircularProgress size={18} /> : <SearchIcon />}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  searchQuery.length >= 2
                    ? <Box sx={{ p: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          No results found for "{searchQuery}"
                        </Typography>
                        <Button
                          startIcon={<AddIcon />}
                          size="small"
                          variant="outlined"
                          onClick={() => openAddForm({ business_name: searchQuery })}
                        >
                          Add as new contact
                        </Button>
                      </Box>
                    : 'Type at least 2 characters to search'
                }
              />
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => openAddForm({ business_name: searchQuery })}
                disabled={searchQuery.length < 2}
                sx={{ flexShrink: 0, height: 56 }}
              >
                Add Contact
              </Button>
            </Box>

            {/* Search results summary */}
            {searchQuery.length >= 2 && !searchLoading && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {searchResults.organisations.length > 0 && (
                  <Alert severity="info" sx={{ py: 0, flex: 1, minWidth: 200 }}>
                    <strong>{searchResults.organisations.length}</strong> existing organisation{searchResults.organisations.length !== 1 ? 's' : ''} found
                  </Alert>
                )}
                {searchResults.contacts.length > 0 && (
                  <Alert severity="warning" sx={{ py: 0, flex: 1, minWidth: 200 }}>
                    <strong>{searchResults.contacts.length}</strong> previous contact{searchResults.contacts.length !== 1 ? 's' : ''} found
                  </Alert>
                )}
              </Box>
            )}
          </Paper>

          {/* Filter bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Filter:</Typography>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, v) => v && setStatusFilter(v)}
              size="small"
            >
              <ToggleButton value="all">All ({contactsTotal})</ToggleButton>
              <ToggleButton value="follow_up">Follow Up</ToggleButton>
              <ToggleButton value="not_interested">Not Interested</ToggleButton>
              <ToggleButton value="converted">Converted</ToggleButton>
              <ToggleButton value="contacted">Contacted</ToggleButton>
            </ToggleButtonGroup>
            <IconButton size="small" onClick={() => loadContacts(statusFilter)} sx={{ ml: 'auto' }}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Contacts table */}
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Business</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contact Person</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone / Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contacted By</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Follow Up</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contactsLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : contacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No contacts yet — use the search bar or Add Contact to get started.
                    </TableCell>
                  </TableRow>
                ) : contacts.map((c) => (
                  <TableRow key={c.id} hover sx={{ opacity: c.status === 'not_interested' ? 0.65 : 1 }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{c.business_name}</Typography>
                      {c.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{c.contact_name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {c.contact_phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{c.contact_phone}</Typography>
                        </Box>
                      )}
                      {c.contact_email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{c.contact_email}</Typography>
                        </Box>
                      )}
                      {!c.contact_phone && !c.contact_email && '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {[c.suburb, c.state].filter(Boolean).join(', ') || c.location || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><StatusChip status={c.status} /></TableCell>
                    <TableCell>
                      <Typography variant="body2">{c.contacted_by_name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmtDate(c.contacted_date)}</Typography>
                    </TableCell>
                    <TableCell>
                      {c.follow_up_date ? (
                        <Typography variant="caption" color="warning.main">
                          {fmtDate(c.follow_up_date)}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {canManage(c) && (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Mark Follow Up">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              setActionDialog({ open: true, contact: c, mode: 'follow_up' });
                              setActionDate(c.follow_up_date?.slice(0, 10) || '');
                              setActionNotes(c.notes || '');
                            }}
                          >
                            <ScheduleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Convert to Offer">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => convertToOffer(c)}
                          >
                            <TrendingUpIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Not Interested">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setActionDialog({ open: true, contact: c, mode: 'not_interested' });
                              setActionDate('');
                              setActionNotes(c.notes || '');
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditForm(c)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deleteContact(c.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ============================================================
          TAB 1 — Reactivation List
      ============================================================ */}
      {tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                Organisations with no orders in the past 2 years. Sales consultants have been
                removed from these accounts — assign a new contact when you reactivate.
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                size="small"
                onClick={loadReactivation}
                disabled={reactivationLoading}
              >
                Refresh
              </Button>
            </Box>
          </Paper>

          <TextField
            placeholder="Filter by name, suburb or consultant…"
            value={reactivationSearch}
            onChange={(e) => setReactivationSearch(e.target.value)}
            size="small"
            sx={{ mb: 2, width: 360 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />

          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Order</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Prev. Consultant</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reactivationLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : filteredReactivation.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {reactivationLoading ? '' : 'No inactive organisations found.'}
                    </TableCell>
                  </TableRow>
                ) : filteredReactivation.map((org) => (
                  <TableRow key={org.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{org.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {org.isSchool   && <Chip label="School"   size="small" color="warning"   variant="outlined" />}
                        {org.isBusiness && <Chip label="Business" size="small" color="secondary" variant="outlined" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {[org.suburb, org.state].filter(Boolean).join(', ') || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {org.primaryPhone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                          <Typography variant="caption">{org.primaryPhone}</Typography>
                        </Box>
                      )}
                      {org.primaryEmail && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                          <Typography variant="caption">{org.primaryEmail}</Typography>
                        </Box>
                      )}
                      {!org.primaryPhone && !org.primaryEmail && '—'}
                    </TableCell>
                    <TableCell>
                      {org.lastOrderDate ? (
                        <Typography variant="caption" color="error.main">
                          {fmtDate(org.lastOrderDate)}
                        </Typography>
                      ) : (
                        <Chip label="Never ordered" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{org.salesConsultantName || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Create Offer">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => convertOrgToOffer(org)}
                          >
                            <TrendingUpIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add to Contact History">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => addReactivationToOutreach(org)}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Organisation">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/app/organizations?id=${org.id}`)}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredReactivation.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {filteredReactivation.length} organisation{filteredReactivation.length !== 1 ? 's' : ''} shown
            </Typography>
          )}
        </Box>
      )}

      {/* ============================================================
          Quick Action Dialog (follow-up / not interested)
      ============================================================ */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, contact: null, mode: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.mode === 'follow_up' ? 'Set Follow-Up Date' : 'Mark Not Interested'}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {actionDialog.mode === 'follow_up' && (
              <TextField
                label="Follow Up Date"
                type="date"
                value={actionDate}
                onChange={(e) => setActionDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField
              label="Notes (optional)"
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder={
                actionDialog.mode === 'not_interested'
                  ? 'Reason they are not interested…'
                  : 'Any notes for the follow-up call…'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, contact: null, mode: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.mode === 'not_interested' ? 'error' : 'warning'}
            onClick={submitActionDialog}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
